/**
 * VotingRecordDots — a compact "did they vote" strip for one voter.
 *
 * Renders one circle per election in `record` (newest first): the circle
 * holds a G / P / S letter (General / Primary / Special) and the 2-digit
 * year sits beneath it. A filled circle means the voter turned out; a
 * hollow outline means they were eligible but didn't. Hover any dot for the
 * full "2024 General — Voted" detail.
 *
 * Presentational only — it takes a pre-computed record, so it works the
 * same whether the data was baked into the static route file at build time
 * or fetched live.
 */
export type ElectionType = 'G' | 'P' | 'S';

export type VotingRecordEntry = {
  year: number;
  type: ElectionType;
  voted: boolean;
};

const TYPE_LABEL: Record<ElectionType, string> = {
  G: 'General',
  P: 'Primary',
  S: 'Special',
};

export function VotingRecordDots({
  record,
  max = 6,
}: {
  record: VotingRecordEntry[];
  max?: number;
}) {
  if (!record || record.length === 0) {
    return <span className="text-[10px] text-slate-400 italic">no recorded elections</span>;
  }

  const shown = record.slice(0, max);

  return (
    <div className="flex items-end gap-1" role="list" aria-label="Voting record">
      {shown.map((e, i) => {
        const yy = String(e.year % 100).padStart(2, '0');
        const title = `${e.year} ${TYPE_LABEL[e.type]} — ${e.voted ? 'Voted' : 'Did not vote'}`;
        return (
          <div key={`${e.year}-${e.type}-${i}`} className="flex flex-col items-center" role="listitem">
            <span
              title={title}
              className={
                'flex h-4 w-4 items-center justify-center rounded-full border text-[8px] font-bold leading-none ' +
                // Background-agnostic on purpose: these popups render on a
                // white Leaflet surface, not the themed app background.
                (e.voted
                  ? 'bg-emerald-600 text-white border-transparent'
                  : 'bg-transparent text-slate-400 border-slate-300')
              }
            >
              {e.type}
            </span>
            <span className="mt-0.5 text-[8px] leading-none text-slate-400 tabular-nums">{yy}</span>
          </div>
        );
      })}
    </div>
  );
}
